import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { 
  ArrowLeft, ChevronDown, ChevronUp, Search, Gamepad2,
  HelpCircle, CreditCard, Shield, Sparkles, Users, Code, Loader2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import useTranslatedItems from '../hooks/useTranslatedItems';
import { useI18n } from '../contexts/I18nContext';

const categories = [
  { id: 'getting-started', name: 'Getting Started', icon: Gamepad2 },
  { id: 'ai-features', name: 'AI Features', icon: Sparkles },
  { id: 'billing', name: 'Billing & Subscriptions', icon: CreditCard },
  { id: 'account', name: 'Account & Security', icon: Shield },
  { id: 'community', name: 'Community & Sharing', icon: Users },
  { id: 'technical', name: 'Technical', icon: Code },
];

const faqs = [
  {
    category: 'getting-started',
    question: 'What is PlayMold?',
    answer: 'PlayMold is an AI-powered game creation platform that lets anyone create playable 3D games using natural language. No coding required! Just describe your game idea and our AI builds it for you.'
  },
  {
    category: 'getting-started',
    question: 'Do I need coding experience?',
    answer: 'No! PlayMold is designed for everyone. You can create games by simply describing what you want in plain English. Our AI handles all the technical details.'
  },
  {
    category: 'getting-started',
    question: 'What types of games can I create?',
    answer: 'You can create various game types including platformers, puzzle games, FPS-style games, RPGs, and simulations. Our template library is constantly growing!'
  },
  {
    category: 'ai-features',
    question: 'How does the AI Game Generator work?',
    answer: 'Simply describe your game concept in natural language (e.g., "Create a spooky mansion with ghosts and collectible items"). Our AI powered by GPT-5.2 interprets your description and generates a complete, playable game scene with objects, enemies, collectibles, and more.'
  },
  {
    category: 'ai-features',
    question: 'What is AI Copilot?',
    answer: 'AI Copilot is your in-editor assistant. While editing your game, you can chat with it to make modifications like "Add 5 more enemies" or "Make the platforms higher". It understands context and makes intelligent changes to your game.'
  },
  {
    category: 'ai-features',
    question: 'Are there limits on AI usage?',
    answer: 'Yes — the editor, gallery, remixing, publishing and templates are all FREE FOREVER. Only high-cost AI features are metered: Free users get 15 Copilot messages/day, 5 AI game generations/day, and 5 AI asset generations/day. Pro raises these to 300 / 100 / 100. Studio is effectively unlimited. Quotas reset daily at 00:00 UTC.'
  },
  {
    category: 'ai-features',
    question: 'Which AI models power PlayMold?',
    answer: 'The Copilot uses Claude Sonnet + GPT-5 for game logic and design. Image / OG social-share cards are generated by Gemini Nano Banana. Sprite and texture assets are generated via fal.ai (FLUX schnell).'
  },
  {
    category: 'billing',
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards (Visa, MasterCard, American Express) through Stripe. All prices shown are in USD; local taxes may be added at checkout.'
  },
  {
    category: 'billing',
    question: 'What tiers are available?',
    answer: 'Free — 15 projects, everything free forever with metered AI. Pro ($12/mo or $115/yr) — unlimited projects, 20× the AI quota, no watermark, HD exports, Featured eligibility, private end-to-end DMs, and Godot .tscn / Unity C# export. Studio ($39/mo or $378/yr) — team seats, priority queue, effectively unlimited AI, 4K exports, custom domain, marketplace revenue share. See the /premium page for the full matrix and instant unlock on payment.'
  },
  {
    category: 'features',
    question: 'How do the export formats work?',
    answer: 'Every finalised game exports as: (1) playable HTML5 self-contained bundle, (2) raw JSON scene, (3) Godot 4 .tscn with real MeshInstance3D nodes + StandardMaterial3D + ext_resource references for asset textures (a companion GDScript is included as a comment for runtime texture download), (4) Unity 2022+ C# MonoBehaviour that recreates the scene with primitives, colors and async remote textures. Godot .tscn and Unity C# exports are Pro/Studio features.'
  },
  {
    category: 'features',
    question: 'Can users chat and follow each other?',
    answer: 'Yes — every creator gets an Inbox at /messages. Follow anyone by clicking their profile in search. Free users\u2019 DMs are visible to admins for moderation; Pro & Studio users can flip the 🔒 Private toggle on any message for end-to-end confidential mode (hidden from admin moderation).'
  },
  {
    category: 'features',
    question: 'Where is my Library?',
    answer: 'Every logged-in user has a **Library** at /library (Dashboard toolbar → folder icon). It holds every file you\u2019ve uploaded — images, audio, video, small game HTML files, model files (.glb/.gltf/.fbx) — and every asset you\u2019ve imported from the Marketplace also sits in your Asset library at /assets. Files in your Library can be dropped into any project you\u2019re building or remixing.'
  },
  {
    category: 'features',
    question: 'Can I undo changes and revert to the last save?',
    answer: 'Yes. In the Editor: **↶ Undo** rolls back your last change in-session (up to 30 steps). **⟲ Revert** rolls back to your last saved checkpoint on the server — with a confirmation warning because unsaved changes are discarded. A pre-revert snapshot is automatically stored so you can undo the revert itself.'
  },
  {
    category: 'features',
    question: 'How long is my trash preserved before deletion?',
    answer: 'Free: 14 days. Pro: 60 days. Studio: 90 days. Admin/Support: 1 year. You can also **Archive** any project (soft-hide with no auto-delete) instead of deleting.'
  },
  {
    category: 'features',
    question: 'Can I sell my own assets on the Marketplace?',
    answer: 'Yes. Free assets — publish directly from your Asset library. **Paid third-party sales** — fill in the marketplace submission form (asset + price + your payout link + description). PlayMold reviews the submission; on approval it goes live with a small platform fee (10% on sales under $20, 5% on $20+, 0% on free / donation). PlayMold reserves the right to remove anything that violates the site policy. **Disclaimer:** third-party items are the responsibility of their creators; PlayMold does not warrant them.'
  },
  {
    category: 'features',
    question: 'Is my data encrypted?',
    answer: 'Yes. All traffic between your browser and PlayMold is TLS/HTTPS. Passwords are bcrypt-hashed. Session tokens are HttpOnly + SameSite cookies. Payments run through Stripe — PlayMold never sees your card details. Private E2E DMs (Pro/Studio) are marked server-side as private and their text is redacted from admin moderation views.'
  },
  {
    category: 'features',
    question: 'What are Editor\u2019s Pick, Staff Pick, and Popular badges?',
    answer: 'Admins can flag any game with curated tags (Staff Pick, Featured, Must Play, Hidden Gem, etc.) and pin one game as Editor\u2019s Pick — that game is force-locked to the top of the Featured carousel on the landing page. Popular This Week / Best Seller / New badges are auto-stamped based on plays, remixes, likes and publish date, so nothing is manually managed except the pin.'
  },
  {
    category: 'billing',
    question: 'Can I cancel my subscription anytime?',
    answer: 'Yes! Cancel any time from Settings. You keep Pro/Studio access until the end of the paid period, then automatically revert to Free.'
  },
  {
    category: 'billing',
    question: 'Is there a free tier?',
    answer: 'Yes — a very generous one. Free forever gives you 15 projects, the full 3D editor, all templates, Community Gallery access, remixing, publishing and daily AI allowances. Most creators never need to upgrade.'
  },
  {
    category: 'account',
    question: 'How do I reset my password?',
    answer: 'Click "Forgot Password" on the login page and enter your email. We\'ll send you a link to reset your password.'
  },
  {
    category: 'account',
    question: 'How do I delete my account?',
    answer: 'Go to Settings → Danger Zone → "Delete my account" and type DELETE to confirm. This is immediate and irreversible — your account, projects, and personal data are removed. This satisfies Google Play\'s data-deletion policy.'
  },
  {
    category: 'account',
    question: 'What happens when I delete a project?',
    answer: 'Deleted projects move to Trash and are hidden from your dashboard and the gallery. Admins can restore them within 30 days on request. Pro/Studio users can self-restore from Settings → My Trash. After 30 days, projects are permanently removed automatically.'
  },
  {
    category: 'account',
    question: 'Is my data secure?',
    answer: 'Yes. We use HTTPS everywhere, bcrypt password hashing, JWT session tokens, and strict access controls. Card data never touches our servers — Stripe handles all payment info. We do not sell your data or run behavioral ad tracking.'
  },
  {
    category: 'community',
    question: 'How do I publish my game?',
    answer: 'In the editor, click "Publish" in the header. Your game appears in the Community Gallery. We also auto-generate a rich social share image via Gemini Nano Banana so links posted on Twitter/Discord show a nice preview card.'
  },
  {
    category: 'community',
    question: 'What is remixing?',
    answer: 'Remixing forks a copy of a published game into your dashboard for you to modify. It\'s the fastest way to learn game design and build on others\' work — remix credit is tracked in the original game\'s stats.'
  },
  {
    category: 'community',
    question: 'Can I make my game private?',
    answer: 'Yes — by default, games are private and only you can see them. When you click "Publish", they enter the Community Gallery. You can Unpublish any time (Editor → Actions), which hides them again but keeps the project in your dashboard.'
  },
  {
    category: 'community',
    question: 'What is Featured Game of the Week?',
    answer: 'Every visit to the Landing and Gallery shows an auto-curated top game, ranked by a weighted score of plays + 5× remixes + 2× likes over the last 7 days (fallback to all-time). Publish and share your game to be eligible.'
  },
  {
    category: 'community',
    question: 'How do I use Templates?',
    answer: 'Head to Templates (in the footer or Landing CTA). Pick a starter — platformer, top-down shooter, puzzle rooms, endless runner, or open sandbox — and click "Use Template". A copy lands in your dashboard, fully editable.'
  },
  {
    category: 'technical',
    question: 'What browsers are supported?',
    answer: 'PlayMold works best on modern browsers including Chrome, Firefox, Safari, and Edge. We recommend Chrome for the best BabylonJS/WebGL experience.'
  },
  {
    category: 'technical',
    question: 'Can I export my game?',
    answer: 'HD web-app export is available on Pro. 4K export lands on Studio. Standalone Android/iOS builds are on the roadmap.'
  },
  {
    category: 'technical',
    question: 'What 3D engine do you use?',
    answer: 'We use Babylon.js — a powerful open-source 3D engine that runs directly in your browser with WebGL/WebGPU.'
  },
  {
    category: 'technical',
    question: 'Which languages does the app support?',
    answer: 'PlayMold is available in 11 languages: English, Español, Français, Deutsch, Italiano, 日本語, 中文, Português, 한국어, हिन्दी and العربية (RTL). Pick a language on the splash screen or switch any time via the language flag menu.'
  },
];

export default function FAQ() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [openItems, setOpenItems] = useState([]);

  const toggleItem = (question) => {
    setOpenItems(prev => 
      prev.includes(question) 
        ? prev.filter(q => q !== question)
        : [...prev, question]
    );
  };

  const { language } = useI18n();

  // Translate on-demand: build a flat array of alternating Q/A strings
  const flatItems = useMemo(() => {
    const arr = [];
    faqs.forEach(f => { arr.push(f.question); arr.push(f.answer); });
    return arr;
  }, []);
  const [translated, translating] = useTranslatedItems('faq', flatItems);
  const localizedFaqs = useMemo(() => {
    if (!translated || translated.length !== flatItems.length) return faqs;
    return faqs.map((f, i) => ({
      ...f,
      question: translated[i * 2] || f.question,
      answer: translated[i * 2 + 1] || f.answer,
    }));
  }, [translated, flatItems]);

  const filteredFaqs = localizedFaqs.filter(faq => {
    const matchesSearch = !searchQuery || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="border-b border-[#1E1E24] bg-[#0A0A0C]/80 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#06B6D4] to-[#0891B2] flex items-center justify-center">
              <Gamepad2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white">PlayMold</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-2xl bg-[#06B6D4]/20 flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-[#06B6D4]" />
            </div>
            <h1 className="text-4xl font-black text-white mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-zinc-400">
              Find answers to common questions about PlayMold
            </p>
            {translating && (
              <div className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full bg-[#06B6D4]/10 border border-[#06B6D4]/30 text-xs text-[#06B6D4]" data-testid="faq-translating">
                <Loader2 className="w-3 h-3 animate-spin" />
                Translating to {language.toUpperCase()}…
              </div>
            )}
          </div>

          {/* Search */}
          <div className="relative mb-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-[#0A0A0C] border-[#1E1E24] text-white"
            />
          </div>

          {/* Categories */}
          <div className="flex flex-wrap gap-2 mb-8">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setSelectedCategory('all')}
              className={selectedCategory === 'all' ? 'bg-[#06B6D4] text-black' : 'text-zinc-400'}
            >
              All
            </Button>
            {categories.map(cat => {
              const Icon = cat.icon;
              return (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={selectedCategory === cat.id ? 'bg-[#06B6D4] text-black' : 'text-zinc-400'}
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {cat.name}
                </Button>
              );
            })}
          </div>

          {/* FAQ Items */}
          <div className="space-y-3">
            {filteredFaqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleItem(faq.question)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  {openItems.includes(faq.question) ? (
                    <ChevronUp className="w-5 h-5 text-[#06B6D4] shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-zinc-500 shrink-0" />
                  )}
                </button>
                {openItems.includes(faq.question) && (
                  <div className="px-5 pb-5 text-zinc-400 leading-relaxed border-t border-[#1E1E24] pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="mt-12 text-center p-8 bg-[#0A0A0C] border border-[#1E1E24] rounded-2xl">
            <h3 className="text-xl font-bold text-white mb-2">Still have questions?</h3>
            <p className="text-zinc-400 mb-6">Our support team is here to help!</p>
            <Button onClick={() => navigate('/contact')} className="bg-[#06B6D4] text-black">
              Contact Support
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
